import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import authApi from '@data/api/auth';
import useJwt from '@data/api/useJwt';
import {
  BooklaRole,
  User,
  UserAuth,
  UserRegistrationData,
} from '@src/@data/types/user';
import { PhoneNumberWithCode } from './PhoneNumberWithCode';
import { NotImplemented } from '@src/@data/utility/NotImplemented';
import { useAnalytics, analyticsEvents } from '@data/store/AnalyticsProvider';
import { logger, logOnError } from '@src/@data/utility/errors';
import { unregisterToken } from '@src/@data/store/NotificationsProvider/utils';
import { getFirebaseMessaging } from '@src/@data/utility/firebase';

interface ContextValueType {
  user?: User;
  createUserPassword: (
    phoneNumber: PhoneNumberWithCode,
    password: string,
    code: string
  ) => Promise<User>;
  loginWithPassword: (
    phoneNumber: PhoneNumberWithCode,
    password: string
  ) => Promise<User>;
  registerUser: (request: UserRegistrationData) => Promise<User>;
  logout: () => void;
  isLoading: boolean;
  notificationToken: string;
  setNotificationToken: (token: string) => void;
  changePassword: (data: {
    current: string;
    password: string;
  }) => Promise<void>;
  isAdminBooklaUser: boolean;
}

export const AuthContext = createContext<ContextValueType>({
  createUserPassword: NotImplemented,
  loginWithPassword: NotImplemented,
  registerUser: NotImplemented,
  logout: NotImplemented,
  isLoading: false,
  notificationToken: '',
  setNotificationToken: NotImplemented,
  changePassword: NotImplemented,
  isAdminBooklaUser: false,
});

export const useAuth = () => useContext(AuthContext);

// eslint-disable-next-line react-hooks/rules-of-hooks
const jwt = useJwt();

export const AuthProvider: React.FC = ({ children }) => {
  const { track, identify, reset } = useAnalytics();

  const [user, setUser] = useState<User>();
  const [isLoading, setIsLoading] = useState(false);
  const [notificationToken, setNotificationToken] = useState('');

  const isAdminBooklaUser = useMemo(
    () => user?.role === BooklaRole.Admin,
    [user?.role]
  );

  const setAuth = (auth: UserAuth) => {
    jwt.setAuth(auth.token);
    localStorage.setItem('user', JSON.stringify(auth.user));
    setUser(auth.user);
    identify(auth.user.id);
    logger.identifyUser(auth.user);
  };

  const clearAuth = () => {
    jwt.clear();
    localStorage.removeItem('user');
    setUser(undefined);
    setIsLoading(false);
  };

  const restoreState = useCallback(() => {
    const userJSON = localStorage.getItem('user') || '';
    if (userJSON) {
      const user = JSON.parse(userJSON);
      setUser(user);
      identify(user.id);
      logger.identifyUser(user);
    } else {
      setUser(undefined);
    }
  }, [identify]);

  const createUserPassword = (
    phoneNumber: PhoneNumberWithCode,
    password: string,
    code: string
  ): Promise<User> => {
    setIsLoading(true);
    return authApi
      .setPassword({ code, password, phoneNumber: phoneNumber.fullNumber })
      .then((auth) => {
        identify(auth.user.id);
        track(analyticsEvents.SIGN_IN);
        setAuth(auth);
        return auth.user;
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const loginWithPassword = (
    phoneNumber: PhoneNumberWithCode,
    password: string
  ): Promise<User> => {
    setIsLoading(true);
    return authApi
      .login(phoneNumber.fullNumber, password)
      .then((auth) => {
        track(analyticsEvents.SIGN_IN);
        setAuth(auth);
        return auth.user;
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const registerUser = (request: UserRegistrationData): Promise<User> => {
    setIsLoading(true);
    return authApi
      .register(request)
      .then((auth) => {
        track(analyticsEvents.SIGN_UP);
        setAuth(auth);
        return auth.user;
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const clearNotificationToken = useCallback(async () => {
    const messaging = getFirebaseMessaging();
    if (notificationToken && messaging) {
      try {
        await unregisterToken(messaging, notificationToken);
        setNotificationToken('');
      } catch (error) {
        logOnError(error);
      }
    }
  }, [notificationToken]);

  const logout = useCallback(async () => {
    setIsLoading(true);
    track(analyticsEvents.LOGOUT);
    await clearNotificationToken();
    reset();
    clearAuth();
    setIsLoading(false);
  }, [clearNotificationToken, reset, track]);

  const changePassword = useCallback(
    async (data: { current: string; password: string }) => {
      try {
        const token = await authApi.changePassword(data);
        jwt.setAuth(token);
      } catch (error) {
        throw error;
      }
    },
    []
  );

  useEffect(() => {
    jwt.addOnUnauthorizedSubscriber(() => {
      logout().then(() => {
        logger.info(`Logged out on unauthorized error`, 'unauthorized');
      });
    });
    if (!jwt.hasAuth()) {
      logout().then(() => {
        logger.info(`Logged out on missing auth`, 'unauthorized');
      });
    } else {
      restoreState();
      setIsLoading(false);
    }
  }, [logout, restoreState]);

  return (
    <AuthContext.Provider
      value={{
        user,
        createUserPassword,
        loginWithPassword,
        registerUser,
        logout,
        isLoading,
        notificationToken,
        setNotificationToken,
        changePassword,
        isAdminBooklaUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
